import { useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { useAtomValue, useSetAtom } from 'jotai/react';
import { useTranslation } from 'next-i18next';
import AgeRestrictedModal from 'src/components/AgeRestrictedModal';
import { DatePicker } from 'src/components/Datepicker';
import VividiAgeConfirmModal from 'src/components/VividiOnly/Register/AgeConfirmModal';
import { socialTokenAtom, tryLoginTypeAtom } from 'src/stores/auth/atoms';
import { closeModalAtom, showModalAtom } from 'src/stores/modal/atoms';
import { signUpSettingsLoadableAtom } from 'src/stores/register/atoms';
import { visitorConfigAtom } from 'src/stores/visitor/atoms';
import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { ModalType } from 'src/types/Modal';
import { calculateAge, checkAgeOverN } from 'src/utils/date';
import { pushEvent } from 'src/utils/event-gateway';
import { Container, NextButton, NoticeText, Section, Title } from './styles';
import type { StepProps } from './types';
const InputContainer = styled(Section)`
  flex: 1;

  align-items: stretch;
  justify-content: center;
`;
const StyledDatePicker = styled(DatePicker)`
  height: 212px;
  border-top: none;
  border-bottom: none;

  --bg-color: ${({
  theme
}) => theme.color.bg1};
  --border-color: transparent;

  .wheel-overlay {
    background: linear-gradient(0deg, var(--bg-color), transparent, var(--bg-color));
    border-top: 1px solid var(--border-color);
    border-bottom: 1px solid var(--border-color);
  }

  ${({
  theme
}) => theme.screenSize.desktop} {
    --bg-color: ${({
  theme
}) => theme.color.bg2};
    --border-color: ${({
  theme
}) => theme.color.surface4};
  }
`;
const StyledNextButton = styled(NextButton)`
  flex-direction: column;
  gap: 2px;
`;
const ButtonSubtitle = styled.span`
  font-size: 12px;
  font-weight: 400;
`;
export default function BirthdayStep({
  onNext,
  className
}: StepProps<Date>) {
  const {
    t
  } = useTranslation();
  const socialToken = useAtomValue(socialTokenAtom);
  const signupDefaultBirthyearOffset = Number(useAtomValue(visitorConfigAtom)?.signupDefaultBirthyearOffset) || 18;
  const signUpSettingsLoadable = useAtomValue(signUpSettingsLoadableAtom);
  const signUpSettings = signUpSettingsLoadable.state === 'hasData' ? signUpSettingsLoadable.data : null;
  const defaultBirth = useMemo(() => {
    if (socialToken?.birth) {
      return socialToken.birth;
    }
    const date = new Date();
    date.setFullYear(date.getFullYear() - signupDefaultBirthyearOffset);
    return date;
  }, [socialToken, signupDefaultBirthyearOffset]);
  const [birth, setBirth] = useState(defaultBirth);
  const [dirty, setDirty] = useState(false);
  const regMinAge = Number(useAtomValue(visitorConfigAtom)?.webRegMinAge) || 18;
  const showModal = useSetAtom(showModalAtom);
  const closeModal = useSetAtom(closeModalAtom);
  const tryLoginType = useAtomValue(tryLoginTypeAtom);
  const handleChange = (value: Date) => {
    setDirty(true);
    setBirth(value);
  };
  const handleSubmit = () => {
    const blockAlertAge = signUpSettings?.birthdaySettings.blockAlertAge;
    const age = calculateAge(birth);

    // 미성년자 경고 나이 체크
    if (blockAlertAge && age <= blockAlertAge) {
      showModal({
        key: ModalType.INVALID_AGE_CHECK,
        component: () => <VividiAgeConfirmModal userAge={age} limitedAge={blockAlertAge} />
      });
      return;
    }

    // 미성년자 밴 나이 체크
    if (!checkAgeOverN(birth, regMinAge)) {
      pushEvent({
        eventType: EVENT_TYPE.SIGN_UP,
        eventName: EVENT_NAME.SCREEN_OPEN__LOGIN_AGE_RESTRICTED,
        eventParams: {
          loginType: tryLoginType
        }
      });
      closeModal(ModalType.LOGIN);
      showModal({
        key: ModalType.AGE_RESTRICTED,
        component: () => <AgeRestrictedModal reason='registration' />
      });
      return;
    }
    onNext(birth);
  };
  useEffect(() => {
    pushEvent({
      eventType: EVENT_TYPE.SIGN_UP,
      eventName: EVENT_NAME.SCREEN_OPEN__LOGIN_BIRTHDAY_INFO,
      eventParams: {
        loginType: tryLoginType
      }
    });
  }, [tryLoginType]);
  return <Container className={className} data-sentry-element="Container" data-sentry-component="BirthdayStep" data-sentry-source-file="Birthday.tsx">
      <Section data-sentry-element="Section" data-sentry-source-file="Birthday.tsx">
        <Title data-sentry-element="Title" data-sentry-source-file="Birthday.tsx">{t('signup_extra_info_birthday')}</Title>
        <NoticeText data-sentry-element="NoticeText" data-sentry-source-file="Birthday.tsx">{t('signup_extra_info_birthday_warning_2')}</NoticeText>
      </Section>
      <InputContainer data-sentry-element="InputContainer" data-sentry-source-file="Birthday.tsx">
        <StyledDatePicker value={birth} onChange={handleChange} data-sentry-element="StyledDatePicker" data-sentry-source-file="Birthday.tsx" />
      </InputContainer>
      <Section data-sentry-element="Section" data-sentry-source-file="Birthday.tsx">
        <StyledNextButton type='button' disabled={!dirty} onClick={handleSubmit} data-sentry-element="StyledNextButton" data-sentry-source-file="Birthday.tsx">
          <span>
            {t('signup_extra_info_birthday_age', {
            age: Math.max(calculateAge(birth), 0)
          })}
          </span>
          <ButtonSubtitle data-sentry-element="ButtonSubtitle" data-sentry-source-file="Birthday.tsx">{t('signup_extra_info_birthday_warning_3')}</ButtonSubtitle>
        </StyledNextButton>
      </Section>
    </Container>;
}